import { defineStore } from 'pinia'
import axios from 'axios'
import { ref } from 'vue'
import api from '@/api'
import AppQueryClient from '@/libs/appQueryClient'

interface InboxAliasesResponse {
  default?: string
  aliases?: string[]
}

const ALIAS_QUERY_KEY = ['alias', 'options'] as const

const EMPTY_ALIASES: InboxAliasesResponse = {
  default: '',
  aliases: [],
}

const isExpectedAliasesFailure = (error: unknown) =>
  axios.isAxiosError(error) || typeof error === 'string'

const toAliasState = (data: InboxAliasesResponse) => {
  const aliases = Array.isArray(data.aliases) ? data.aliases : []

  return {
    default: typeof data.default === 'string' ? data.default : '',
    options: aliases.map((item) => ({ label: item, value: item })),
  }
}

const useAliasesStore = defineStore('aliasesStore', () => {
  const state = ref<{
    default: string
    options: AnyRecord[]
  }>({
    default: '',
    options: [],
  })

  const getAliasesApi = async () => {
    try {
      return await AppQueryClient.fetchQuery({
        staleTime: Infinity,
        queryKey: ALIAS_QUERY_KEY,
        queryFn: async () => {
          const data = (await api.get('/inbox/aliases')) as InboxAliasesResponse
          state.value = toAliasState(data)
          return data
        },
      })
    } catch (error) {
      // Keep Ticket entry available. Leave the query in error so the next enter can retry.
      if (isExpectedAliasesFailure(error)) {
        state.value = toAliasState(EMPTY_ALIASES)
        return EMPTY_ALIASES
      }

      throw error
    }
  }

  const getDefaultValue = () => state.value.default

  const getOptions = () => state.value.options

  return {
    getOptions,
    getAliasesApi,
    getDefaultValue,
  }
})

export default useAliasesStore
